import React, { Component } from 'react'
import { connect } from "react-redux"
import { IOptionProps, Option as SelectOption, Label as SelectLabel } from "react-select-item";
import Select from "react-select"

import trans from "trans";

import {
    css,
    confirm,
    Icon,
    Theme,
    Grid,
    GridCell,
    Spinner,
    Button,
    FieldLabel,
} from "@bdfx/mergado-ui-kit"

import {Avatar} from '@mui/material'

import { WatermarkGroupType } from "../products/types";

import { URL } from "app/config/api-config";
import { createNewWatermarkGroup, deleteWatermarkGroup, getWatermarkGroups } from "app/helpers/api";
import { AxiosResponse } from "axios";
import { stringToHash } from "../shape-edit/forms/helpers";
import { getLocale } from "app/helpers/session";
import { ReduxList } from 'app/types/app-types';
import _find from 'lodash/find';
import { setWatermarkGroupsToStore } from './reducers';

const iconSize = 20
export const SHOW_MAX_IMAGES = 1
export const MIN_IMAGES_WITH_MASKS_TO_PUBLISH = 10
export const MAX_IMAGES_WITH_MASKS_TO_PUBLISH = 15

interface Props {
    watermarkGroups: ReduxList<WatermarkGroupType[]>
    setWatermarkGroupsToStore: typeof setWatermarkGroupsToStore
    selectedGroupId: number
    setSelectedGroup: Function
    variant: 'tiles' | 'select'
    maxImages?: number
    setShowAsModal: Function
}

interface State {
    renderHash: number
}

class WatermarkGroupsSelect extends Component<Props, State> {
    constructor(props) {
        super(props)

        this.state = {
            renderHash: this.getGroupsHashes(props.watermarkGroups)
        }
    }

    getGroupsHashes = (watermarkGroups: ReduxList<WatermarkGroupType[]>): number => {
        if (watermarkGroups.isLoaded) {
            return stringToHash((watermarkGroups.data.map(w => w.group_hash).join('')))
        }

        return null
    }

    componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>, snapshot?: any): void {
        if (prevProps && prevProps.watermarkGroups.data) {
            const groupHashesBefore = this.getGroupsHashes(prevProps.watermarkGroups)
            const groupHashesAfter = this.getGroupsHashes(this.props.watermarkGroups)

            if (prevProps.selectedGroupId !== this.props.selectedGroupId || groupHashesBefore !== groupHashesAfter) {
                this.setState({ renderHash: groupHashesAfter })
            }
        }
    }

    updateWatermarkGroups = () => {
        getWatermarkGroups()
            .then((response: AxiosResponse<WatermarkGroupType[]>) => {
                this.props.setWatermarkGroupsToStore(response.data)
            })
            .catch(e => {
                console.error('cannot get watermark groups', e)
            })
    }

    createNewGroupModal = () => {
        createNewWatermarkGroup('')
            .then((response: AxiosResponse<WatermarkGroupType>) => {
                const newGroup = response.data
                this.props.setSelectedGroup(newGroup.id)
                this.updateWatermarkGroups()
                this.props.setShowAsModal(true)
            })
            .catch(e => console.error('cannot create new group', e))
    }

    handleDeleteWatermarGroup = (g: WatermarkGroupType) => {
        return confirm({
            message: <Grid cols={'auto auto'} gap={'10px'}>
                <GridCell style={{ alignSelf: 'center' }}>{trans('confirm_remove_group')} <strong style={{ color: Theme.steelblue }}>{g.name}</strong> ({g.images.length} {trans('count_images')})?</GridCell>
                <GridCell></GridCell>
            </Grid>,
            buttons: [
                <Button key="yes" onClick={() => {
                    deleteWatermarkGroup(g.id)
                        .then((response: AxiosResponse<any>) => {
                            this.updateWatermarkGroups()
                        })
                        .catch(e => {
                            console.error('cannot delete group', e)
                        })
                }}>{trans('yes')}</Button>,
                <Button key="no" onClick={() => false}>{trans('no')}</Button>
            ]
        })
    }

    render() {
        const { watermarkGroups, selectedGroupId, variant, maxImages } = this.props
        const { renderHash } = this.state

        const locale = getLocale().replace('_', '-')
        const n = maxImages ? maxImages : SHOW_MAX_IMAGES

        const selectedGroup = (selectedGroupId && watermarkGroups.isLoaded) ? _find(watermarkGroups.data, (watermarkGroup: WatermarkGroupType) => {
            return watermarkGroup.id === selectedGroupId
        }) : null

        const selectOptions: SelectOption[] = (variant === 'select' && watermarkGroups) ? watermarkGroups.data.map((g) => {
            const publishedAt = g.published_at ? new Date(g.published_at) : null

            return {
                label: <div className={'wg-option'} title={`group ${g.id}`} key={`gr${g.id}`}>
                    <span>{g.name}</span>
                    <span>{publishedAt ? `${publishedAt.toLocaleDateString(locale)} ${publishedAt.toLocaleTimeString(locale)}` : trans('not_published_yet')}</span>
                    <span>{g.images.slice(0, n).map(i => {
                        const hash = (i.user_set_objects ? stringToHash(i.user_set_objects) : '')

                        const sourceImageUrl = `${URL.laravelBase}get-wmgroup-image/${g.project_id}/${g.id}/${i.filename}?t=${hash}`
                        const maskedImageUrl = `${URL.laravelBase}get-wmgroup-image-masked/${g.project_id}/${g.id}/${i.filename}?t=${hash}`

                        return <img
                            key={`option_${hash}_{i.filename}_{i.id}`}
                            src={i.user_set_objects ? maskedImageUrl : sourceImageUrl}
                            className={'option-images'}
                        />
                    }
                    )}</span>
                    {g.images.length > n && n > 1 && '...'}
                    <span>({g && g.images ? g.images.filter(i => i.user_set_objects).length : 0})</span>
                </div>,
                value: `${g.id}`,
                disabled: false, /* !publishedAt */
            }
        }) : []

        const newGroupOption: SelectOption = {
            label: `--- ${trans('new_group')} ---`,
            value: '',
            disabled: false,
        }

        const selected: SelectOption = selectOptions && (selectedGroupId && watermarkGroups.isLoaded) ? _find(selectOptions, (option) => {
            return `${option.value}` === `${selectedGroupId}`
        }) : null

        return (
            <React.Fragment>
                {variant === 'tiles' &&
                    <StyledGrid>
                        <GridCell>
                            <StyledSubGrid className={'shadow-box clickable'} onClick={(e) => this.createNewGroupModal()} dataActive={selectedGroup && selectedGroup.id}>
                                <GridCell col={'1/-1'} style={{ alignSelf: 'center !important' }}>
                                    <Icon type={'plus-square-o'} size={56} color={Theme.steelblue} />
                                    <div style={{
                                        color: Theme.steelblue,
                                        fontSize: '21px',
                                        padding: '5px 20px',
                                        whiteSpace: "pre-wrap"
                                    }}>
                                        {trans('create_watermark_group')}
                                    </div>
                                </GridCell>
                            </StyledSubGrid>

                            <ErrorDiv style={{ borderTop: `1px solid transparent` }} />
                        </GridCell>

                        {!watermarkGroups &&
                            <GridCell><Spinner type={'bubbles'} /></GridCell>
                        }

                        {watermarkGroups && watermarkGroups.data.map((g, k) => {
                            const locale = getLocale().replace('_', '-')
                            const publishedAt = g.published_at ? new Date(g.published_at) : null

                            return <GridCell key={`group_${k}`}>
                                <StyledSubGrid className={'shadow-box clickable'} onClick={() => this.props.setSelectedGroup(g.id)} dataActive={selectedGroup && selectedGroup.id === g.id}>
                                    {g.images && Array.isArray(g.images) &&
                                        g.images.slice(0, n).map((i, j) => {
                                            const hash = (i.user_set_objects ? stringToHash(i.user_set_objects) : '')
                                            const sourceImageUrl = `${URL.laravelBase}get-wmgroup-image/${g.project_id}/${g.id}/${i.filename}?t=${hash}`
                                            const maskedImageUrl = `${URL.laravelBase}get-wmgroup-image-masked/${g.project_id}/${g.id}/${i.filename}?t=${hash}`

                                            return <GridCell key={`image_${j}`}>
                                                <img key={`opt${hash}`} src={i.user_set_objects ? maskedImageUrl : sourceImageUrl} className={'option-images'} />
                                            </GridCell>
                                        }
                                        )}

                                    <GridCell col={'1/-1'} className={'option-label'} style={{ position: 'relative' }}>
                                        <div>{g.name}</div>
                                        <div className={'small'}>
                                            {publishedAt ?
                                                <span>{publishedAt.toLocaleDateString(locale)} {publishedAt.toLocaleTimeString(locale)}</span> :
                                                <span>?</span>}
                                        </div>

                                        <Avatar
                                            sx={{ bgcolor: '#eeeeee38', width: '30px', height: '30px' }}
                                            className={'clickable'}
                                            title={trans("delete")}
                                            onClick={() => this.handleDeleteWatermarGroup(g)}
                                            style={{
                                                position: 'absolute',
                                                bottom: '2px',
                                                right: '2px'
                                            }}><Icon type={'trash'} color={Theme.red} size={iconSize} />
                                        </Avatar>
                                    </GridCell>
                                </StyledSubGrid>

                                <ErrorDiv style={{ borderTop: `1px solid ${g.published_at ? 'transparent' : Theme.red}` }}>
                                    {!g.published_at ?
                                        trans('unpublished_group') :
                                        g.group_hash !== g.published_hash ?
                                            trans('watermark_group_hash_diff') :
                                            ''
                                    }
                                </ErrorDiv>
                            </GridCell>
                        }
                        )}
                    </StyledGrid>
                }

                {variant === 'select' &&
                    <React.Fragment>
                        <FieldLabel>{trans('watermark_groups')}</FieldLabel>
                        <StyledSelect
                            key={renderHash}
                            options={[newGroupOption, ...selectOptions]}
                            value={selected}
                            multiple={false}
                            closeOnChange={true}
                            onClear={() => this.props.setSelectedGroup(null)}
                            onChange={(e: SelectOption) => {
                                if (e.value === '') {
                                    this.props.setSelectedGroup(null)
                                    this.createNewGroupModal()
                                } else {
                                    this.props.setSelectedGroup(e.value)
                                }
                            }}
                        />
                    </React.Fragment>
                }
            </React.Fragment>
        )
    }
}

const StyledSelect = css(Select)({
    ' .wg-option': {

    },
    ' .option-label': {
        padding: '20px 0',
        lineHeight: '30px',
    },
    ' img.option-images': {
        paddingTop: '8px',
        maxWidth: '28px',
        maxHeight: '28px',
    },
    ' .published-at': {
        marginLeft: '15px',
    },
    ' .with-mask': {
        marginLeft: '15px',
    },
    ' .group-name': {
        marginLeft: '15px',
    },
    " span .label-wrapper div": {
        display: 'block',
    },
    "[class*=singleValue]": {
        ".label-wrapper": {
            fontSize: "14px",
        },
    },
    ' .label-wrapper': {
        fontSize: "10px",
        overflow: 'hidden',
        ' img': {
            paddingTop: '8px',
        },
    },
    ' .option-image': {
        maxWidth: '100%',
    },
    ' .float-right': {
        float: 'right',
        color: Theme.blue,
    },
    ' input.react-select-item-search': {
        left: '100px',
        width: 'calc(100% - 20px)',
    },
    ' .react-select-item-options': {
        backgroundColor: '#fff',
    },
    ' .react-select-item-option, .react-select-item-option-selected': {
        /* padding: '8px 10px 2px 10px', */
    },
    ' div[class$="-menu"]': {
        zIndex: 99,
    }
})

const OptionDiv = ({ option }) => {
    return <div style={{ display: 'flex' }} title={option.label || ''}>
        {option.images && option.images}
        {option.publishedAt && <span className={'published-at'}> ({option.publishedAt})</span>}
        {option.imagesWithMaskCount !== null && <span className={'with-mask'}>{option.imagesWithMaskCount}/{MIN_IMAGES_WITH_MASKS_TO_PUBLISH}</span>}
        {option.label && <span className={'group-name'}>{option.label}</span>}
    </div>
}

const StyledGrid = css(Grid)({
    gridTemplateColumns: 'repeat(auto-fit, 160px)',
    gridGap: '20px',
    ' .muk-gridcell': {
        alignSelf: 'center',
        textAlign: 'center',
    },
    marginBottom: '20px',
})

const StyledSubGrid = css(Grid)({
    gridTemplateColumns: 'repeat(3, 1fr)',
    width: '160px',
    height: '160px',
    borderRadius: Theme.radius,
    background: '#f9f9f9',
    '&:hover img.option-images': {
        transition: "transform 0.8s",
        transform: "scale(1.2)",
    },
    ' img.option-images': {
        maxWidth: '38px',
        maxHeight: '32px',
    },
    ' .muk-gridcell': {
        alignSelf: 'end',
        textAlign: 'center',
    },
    ' div.option-label': {
        background: Theme.steelblue,
        color: '#ffffff',
        borderBottomRightRadius: Theme.radius,
        borderBottomLeftRadius: Theme.radius,
        padding: '4px 0',
    },
    ' .option-label .name': {
        fontWeight: 'bold',
        fontSize: '20px',
    },
    ' .option-label .small': {
        fontSize: '8px',
    },
}, props => {
    return props.dataActive ? {
        boxShadow: '0 0 3px 1px rgba(75,115,142,.8)',
        webkitBoxShadow: '0 0 3px 1px rgba(75,115,142,.8)',
        mozBoxShadow: '0 0 3px 1px rgba(75,115,142,.8)',
    } : {
        boxShadow: '0 0 2px 1px rgba(0,0,0,.3)',
        webkitBoxShadow: '0 0 2px 1px rgba(0,0,0,.3)',
        mozBoxShadow: '0 0 2px 1px rgba(0,0,0,.3)',
    }
})

const ErrorDiv = css('div')({
    height: '18px',
    lineHeight: '12px',
    paddingTop: '4px',
    fontSize: '10px',
    color: Theme.red,
    textAlign: 'center',
})

function mapStateToProps(state) {
    return {
        watermarkGroups: state.watermarkGroups,
    }
}

export default connect(mapStateToProps, {
    setWatermarkGroupsToStore,
})(WatermarkGroupsSelect)
